import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import M365API from "./M365.api";
import {
  M365GetAuthUrlResponse,
  M365GetIntegrationResponse,
  M365GetUsersQuery,
  M365SaveOAuth2CredentialsData,
  M365UseGetInfiniteUsers,
  M365UseGetUsers,
} from "./M365.types";
import { queryClient } from "@/index";
import { IntegrationScope } from "../integrations.types";

const M365QueryKeys = {
  integration: (query?: any) =>
    !!query ? ["m365", "integration", query] : ["m365", "integration"],
  authUrl: ["m365", "authUrl"],
  authToken: ["m365", "authToken"],
  users: (query?: M365GetUsersQuery) =>
    !!query ? ["m365", "users", query] : ["m365", "users"],
};

const useGetIntegration = (values: {
  query?: {};
  options?: Omit<UseQueryOptions, "queryKey" | "queryFn">;
}) => {
  return useQuery({
    queryKey: M365QueryKeys.integration(values.query),
    queryFn: () => M365API.getIntegration(values.query),
    ...values.options,
  }) as UseQueryResult<M365GetIntegrationResponse, Error>;
};

const resetQuery = () => {
  queryClient.resetQueries({
    queryKey: M365QueryKeys.integration(),
  });
};

const useGetAuthUrl = (options?: UseQueryOptions) => {
  return useQuery({
    queryKey: M365QueryKeys.authUrl,
    queryFn: M365API.getAuthUrl,
    ...options,
  }) as UseQueryResult<M365GetAuthUrlResponse, Error>;
};

const useGetUsers: M365UseGetUsers = (query, options) => {
  return useQuery({
    queryKey: M365QueryKeys.users(query),
    queryFn: () => M365API.getUsers(query),
    ...options,
  });
};

const useGetInfiniteUsers: M365UseGetInfiniteUsers = (query, options) => {
  return useInfiniteQuery({
    queryKey: M365QueryKeys.users(query),
    queryFn: async ({ pageParam }) => {
      const result = await M365API.getUsers({
        ...query,
        page: pageParam,
      });
      if (result?.success === false) throw new Error("Failed to fetch users");
      return result;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
    initialPageParam: "",
    placeholderData: (data) => data,
    ...options,
  });
};

const useSaveClientCredentials = () => {
  return useMutation<
    {
      data: {
        integration_id: number;
      };
    },
    Error,
    {
      client_id: string;
      client_secret: string;
      tenant_id: string;
      scope: IntegrationScope;
    }
  >({
    mutationFn: (values) => M365API.saveClientCredentials(values),
  });
};

const useSaveOAuth2Credentials = (
  values: M365SaveOAuth2CredentialsData,
  options?: Omit<UseQueryOptions, "queryKey" | "queryFn">
) => {
  return useQuery({
    queryKey: M365QueryKeys.authToken,
    queryFn: () => M365API.saveOAuth2Credentials(values),
    ...options,
  });
};

export const M365Query = {
  M365QueryKeys,
  useGetIntegration,
  resetQuery,
  useGetAuthUrl,
  useSaveOAuth2Credentials,
  useGetUsers,
  useGetInfiniteUsers,
  useSaveClientCredentials,
};
